'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import type { LogisticsJob } from './types';

// ─── Props ────────────────────────────────────────────────────────────────────

interface KpiStripProps {
  jobs: LogisticsJob[];
  onAtRiskClick?: () => void;
}

// ─── Helper Functions (module scope — stable across re-renders) ───────────────

function countJobsInPipeline(jobs: LogisticsJob[]): number {
  return jobs.filter(
    j => j.materials_status !== 'Delivered' || j.dumpster_status !== 'Removed'
  ).length;
}

function countProductionJobs(jobs: LogisticsJob[]): number {
  return jobs.filter(j =>
    j.materials_status === 'Pending' || j.materials_status === 'Ordered'
  ).length;
}

function countPermitsJobs(jobs: LogisticsJob[]): number {
  const permitActiveStatuses = ['not_started', 'needs_filing', 'filed', 'pending_review'];
  return jobs.filter(j => {
    const ps = j.permit_status ?? 'not_started';
    return permitActiveStatuses.includes(ps);
  }).length;
}

function countScheduledJobs(jobs: LogisticsJob[]): number {
  return jobs.filter(
    j => j.materials_status === 'En Route' || j.dumpster_status === 'Placed'
  ).length;
}

function countAtRiskJobs(jobs: LogisticsJob[]): number {
  const seen = new Set<string>();
  jobs.forEach(j => {
    const hasRiskFlag = j.risk_flag === true;
    const hasHighAlert = j.alerts?.some(a => a.severity === 'high') ?? false;
    if (hasRiskFlag || hasHighAlert) seen.add(j.id);
  });
  return seen.size;
}

function computeOnTimePercent(jobs: LogisticsJob[]): { display: string; hexColor: string } {
  if (jobs.length === 0) return { display: '—', hexColor: '#6b7280' };
  const onTimeStatuses = ['Loaded', 'Delivered', 'En Route'];
  const onTimeCount = jobs.filter(j => {
    const noHighAlert = !(j.alerts?.some(a => a.severity === 'high') ?? false);
    const goodStatus = onTimeStatuses.includes(j.materials_status);
    return noHighAlert && goodStatus;
  }).length;
  const pct = Math.round((onTimeCount / jobs.length) * 100);
  let hexColor = '#dc2626';
  if (pct >= 98) hexColor = '#16a34a';
  else if (pct >= 90) hexColor = '#F5A623';
  return { display: `${pct}%`, hexColor };
}

function computeAvgQuoteToInstall(jobs: LogisticsJob[]): { display: string; hexColor: string } {
  const diffs: number[] = [];
  jobs.forEach(j => {
    try {
      if (!j.quote_date || !j.installation_date) return;
      const qd = new Date(j.quote_date).getTime();
      const id = new Date(j.installation_date).getTime();
      if (isNaN(qd) || isNaN(id)) return;
      const days = Math.round((id - qd) / (1000 * 60 * 60 * 24));
      diffs.push(days);
    } catch {
      // silently skip malformed dates
    }
  });
  if (diffs.length === 0) return { display: '—', hexColor: '#6b7280' };
  const avg = Math.round(diffs.reduce((a, b) => a + b, 0) / diffs.length);
  let hexColor = '#dc2626';
  if (avg <= 10) hexColor = '#16a34a';
  else if (avg <= 14) hexColor = '#1C2E5E';
  return { display: `${avg} days`, hexColor };
}

function countWeatherAlerts(jobs: LogisticsJob[]): number {
  return jobs.filter(j => j.alerts?.some(a => a.type === 'weather')).length;
}

function getWeatherAlertMarkets(jobs: LogisticsJob[]): string[] {
  const cities: string[] = [];
  jobs.forEach(j => {
    if (j.alerts?.some(a => a.type === 'weather')) {
      try {
        const parts = j.address.split(',');
        if (parts.length >= 2) {
          const city = parts[1].trim();
          if (city && !cities.includes(city)) cities.push(city);
        }
      } catch {
        // skip
      }
    }
  });
  return cities;
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function generateSparklinePoints(seedValue: number): string {
  // Deterministic pseudo-random based on seedValue — no flicker on re-render
  const points: [number, number][] = [];
  const count = 14;
  const w = 100;
  const h = 16;
  let prev = seedValue;
  for (let i = 0; i < count; i++) {
    // Simple LCG seeded on seedValue + i
    const seed = (seedValue * 9301 + i * 49297 + 233) % 233280;
    const rand = seed / 233280;
    const val = 90 + rand * 10; // 90–100 range
    prev = val;
    const x = (i / (count - 1)) * w;
    const y = h - ((val - 90) / 10) * h;
    points.push([x, y]);
  }
  return points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
}

// ─── Tile wrapper components ──────────────────────────────────────────────────

interface TileProps {
  label: string;
  iconName: string;
  iconColor: string;
  iconBg: string;
  value: React.ReactNode;
  valueColor: string;
  subLine: React.ReactNode;
  subLineText?: string; // for title attr
  onClick?: () => void;
  ariaLabel?: string;
}

function Tile({
  label,
  iconName,
  iconColor,
  iconBg,
  value,
  valueColor,
  subLine,
  subLineText,
  onClick,
  ariaLabel,
}: TileProps) {
  const cardClass =
    'bg-white shadow-sm border border-gray-100 p-4 transition-all';
  const cardStyle: React.CSSProperties = { borderRadius: 12, minHeight: 100 };

  const inner = (
    <>
      {/* Top row */}
      <div className="flex items-center gap-2">
        <div
          aria-hidden="true"
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}
        >
          <Icon name={iconName as any} size={16} style={{ color: iconColor }} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 truncate">
          {label}
        </span>
      </div>

      {/* Value */}
      <div
        className="text-2xl font-bold mt-3 md:text-2xl"
        style={{ color: valueColor }}
      >
        {value}
      </div>

      {/* Sub-line */}
      <div
        className="text-[11px] text-gray-500 leading-tight mt-1.5 truncate"
        title={subLineText}
      >
        {subLine}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        role="group"
        aria-label={ariaLabel ?? label}
        onClick={onClick}
        className={`${cardClass} cursor-pointer hover:bg-red-50/30 text-left w-full`}
        style={cardStyle}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      role="group"
      aria-label={label}
      className={`${cardClass} cursor-default`}
      style={cardStyle}
    >
      {inner}
    </div>
  );
}

// ─── KpiStrip Component ───────────────────────────────────────────────────────

export default function KpiStrip({ jobs, onAtRiskClick }: KpiStripProps) {
  const [coCount, setCoCount] = useState<number | null>(null);
  const [coImpact, setCoImpact] = useState<number>(0);

  useEffect(() => {
    const fetchChangeOrders = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('change_orders')
          .select('amount, status')
          .neq('status', 'completed')
          .neq('status', 'signed')
          .neq('status', 'rejected');
        if (error) throw error;
        const count = data?.length ?? 0;
        const impact = (data ?? []).reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
        setCoCount(count);
        setCoImpact(impact);
      } catch {
        setCoCount(0);
        setCoImpact(0);
      }
    };
    fetchChangeOrders();
  }, []);

  // ── Tile 1: Jobs in Pipeline ──
  const pipelineCount = countJobsInPipeline(jobs);
  const productionCount = countProductionJobs(jobs);
  const permitsCount = countPermitsJobs(jobs);
  const scheduledCount = countScheduledJobs(jobs);
  const pipelineSubText = `${productionCount} production · ${permitsCount} permits · ${scheduledCount} scheduled`;

  // ── Tile 2: At Risk ──
  const atRiskCount = countAtRiskJobs(jobs);

  // ── Tile 3: On-Time % ──
  const onTime = computeOnTimePercent(jobs);
  const sparklinePoints = generateSparklinePoints(
    onTime.display === '—' ? 95 : parseInt(onTime.display, 10)
  );

  // ── Tile 4: Avg Quote-to-Install ──
  const avgQTI = computeAvgQuoteToInstall(jobs);

  // ── Tile 5: Open Change Orders ──
  const coLoading = coCount === null;

  // ── Tile 6: Weather Alerts ──
  const weatherCount = countWeatherAlerts(jobs);
  const weatherMarkets = getWeatherAlertMarkets(jobs);
  const weatherSubText =
    weatherCount > 0
      ? (() => {
          const shown = weatherMarkets.slice(0, 3).join(', ');
          return weatherMarkets.length > 3 ? `${shown}…` : shown;
        })()
      : 'Clear across all markets';

  return (
    <div className="mb-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">

        {/* Tile 1 — Jobs in Pipeline */}
        <Tile
          label="JOBS IN PIPELINE"
          iconName="BriefcaseIcon"
          iconColor="#1C2E5E"
          iconBg="#eff6ff"
          value={<span style={{ color: '#1C2E5E' }}>{pipelineCount}</span>}
          valueColor="#1C2E5E"
          subLine={pipelineSubText}
          subLineText={pipelineSubText}
        />

        {/* Tile 2 — At Risk */}
        <Tile
          label="AT RISK"
          iconName="ExclamationTriangleIcon"
          iconColor="#dc2626"
          iconBg="#fee2e2"
          value={
            <span style={{ color: atRiskCount > 0 ? '#dc2626' : '#6b7280' }}>
              {atRiskCount}
            </span>
          }
          valueColor={atRiskCount > 0 ? '#dc2626' : '#6b7280'}
          subLine={atRiskCount > 0 ? 'Needs attention today' : 'All clear'}
          subLineText={atRiskCount > 0 ? 'Needs attention today' : 'All clear'}
          onClick={() => onAtRiskClick?.()}
          ariaLabel="Filter queue to at-risk jobs"
        />

        {/* Tile 3 — On-Time % */}
        <Tile
          label="ON-TIME % (30D)"
          iconName="CheckBadgeIcon"
          iconColor="#16a34a"
          iconBg="#dcfce7"
          value={
            <span
              style={{
                color: onTime.display === '—' ? '#9ca3af' : onTime.hexColor,
              }}
            >
              {onTime.display}
            </span>
          }
          valueColor={onTime.hexColor}
          subLine={
            <svg
              width="100%"
              height="16"
              viewBox="0 0 100 16"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <polyline
                points={sparklinePoints}
                fill="none"
                stroke={onTime.display === '—' ? '#9ca3af' : onTime.hexColor}
                strokeWidth={1.5}
              />
            </svg>
          }
          subLineText="On-time trend (30 days)"
        />

        {/* Tile 4 — Avg Quote-to-Install */}
        <Tile
          label="AVG QUOTE-TO-INSTALL"
          iconName="ClockIcon"
          iconColor="#E89A3C"
          iconBg="#e0f2fe"
          value={
            <span
              style={{
                color: avgQTI.display === '—' ? '#9ca3af' : avgQTI.hexColor,
              }}
            >
              {avgQTI.display}
            </span>
          }
          valueColor={avgQTI.hexColor}
          subLine="Target: &lt;14 days"
          subLineText="Target: <14 days"
        />

        {/* Tile 5 — Open Change Orders */}
        <Tile
          label="OPEN CHANGE ORDERS"
          iconName="DocumentTextIcon"
          iconColor="#F5A623"
          iconBg="#fef3c7"
          value={
            coLoading ? (
              <span style={{ color: '#9ca3af' }}>—</span>
            ) : (
              <span style={{ color: (coCount ?? 0) > 0 ? '#1C2E5E' : '#6b7280' }}>
                {coCount}
              </span>
            )
          }
          valueColor={(coCount ?? 0) > 0 ? '#1C2E5E' : '#6b7280'}
          subLine={
            coLoading
              ? 'Loading…'
              : (coCount ?? 0) > 0
              ? `${formatCurrency(coImpact)} impact`
              : "No open CO's"
          }
          subLineText={
            coLoading
              ? 'Loading…'
              : (coCount ?? 0) > 0
              ? `${formatCurrency(coImpact)} impact`
              : "No open CO's"
          }
        />

        {/* Tile 6 — Weather Alerts */}
        <Tile
          label="WEATHER ALERTS"
          iconName="CloudIcon"
          iconColor={weatherCount > 0 ? '#dc2626' : '#6b7280'}
          iconBg={weatherCount > 0 ? '#fee2e2' : '#f3f4f6'}
          value={
            <span style={{ color: weatherCount > 0 ? '#dc2626' : '#6b7280' }}>
              {weatherCount}
            </span>
          }
          valueColor={weatherCount > 0 ? '#dc2626' : '#6b7280'}
          subLine={weatherSubText}
          subLineText={weatherMarkets.join(', ') || 'Clear across all markets'}
        />

      </div>
    </div>
  );
}
